import moment from 'moment'
import React, { ReactElement, ReactNode, useEffect, useState } from 'react'
import styled from 'styled-components'
import {
  Buttonlink,
  formatCurrency,
  FormattedDateTime,
  hasRole,
  IGiftCardResponse,
  Loading,
  Role,
} from '@one-tree/library'
import { useHistory } from 'react-router-dom'
import { toSentenceCase } from '../../helpers/DataTransformer'
import { transformDate, TransformDateFormat } from '../../helpers/DateHelper'
import {
  GiftCardResourceOptions,
  Resource,
  IGiftCardHistory,
} from '../../types/API'
import { get } from '../../helpers/APIHelper'
import TableHeader from './resultTable/TableHeader'
import { voucherWord } from '../../helpers/FormatHelper'
import { useOrganisation } from '../../context/OrganisationProvider'
import { RoutePath } from '../../types/Routes'

const HistoryInfo = styled.div`
  display: inline-flex;
  ul {
    list-style: none;
    padding-left: 15px;
    margin: 0;
  }
`
export interface IHistoryProps {
  giftCard: IGiftCardResponse
}

function History(props: IHistoryProps): ReactElement {
  const { giftCard } = props
  const [cardHistory, setCardHistory] = useState<IGiftCardHistory[]>([])
  const [loading, setLoading] = useState(false)
  const { organisation, organisationUser } = useOrganisation()
  const { currencyCode } = organisation || {}

  const { role } = organisationUser || {}
  const isUser = role && hasRole(role, Role.OrganisationUser)

  const getHistory = async (): Promise<void> => {
    setLoading(true)
    const result = await get<IGiftCardHistory[]>({
      resource: Resource.GiftCards,
      urlPath: [giftCard.id, GiftCardResourceOptions.History],
    })
    if (result) setCardHistory(result)
    setLoading(false)
  }
  useEffect(() => {
    getHistory()
  }, [giftCard])

  const history = useHistory()
  const goTo = (targetId: number | undefined): void => {
    if (targetId) {
      history.push(
        `${RoutePath.VoucherDetail}/${targetId}`,
        history.location.state, // Preserve the state (from sales table or home page)
      )
    }
  }

  const renderHistory = cardHistory.map((item: IGiftCardHistory) => (
    <tr key={item.id}>
      <th aria-label="Date">
        <FormattedDateTime timestamp={item.date} />
      </th>
      <td>
        <HistoryInfo>
          {`${item.emailAddress || '-'}`}
          <ul>
            {Object.entries(item.logData).map((logItem) => {
              const isRedemptionValue = logItem[0] === 'redemptionValue'

              let value: ReactNode = logItem[1].newValue
              if (
                !isRedemptionValue
                && typeof logItem[1].newValue === 'number'
                && moment(logItem[1].newValue).isValid()
              ) {
                value = transformDate(
                  logItem[1].newValue,
                  TransformDateFormat.NumbersAndSlashes,
                )
              }

              if (isRedemptionValue && typeof value === 'number') {
                value = currencyCode && formatCurrency(value, currencyCode)
              }

              if (
                typeof value === 'string'
                && value.toLowerCase() === 'delivered'
              ) value = 'dispatched'

              if (isUser && logItem[0] === 'childCard') {
                value = (
                  <Buttonlink
                    onClick={(): void => goTo(Number(logItem[1].newValue))}
                  >
                    {logItem[1].newValue}
                  </Buttonlink>
                )
              }

              const keyWithHTML = <strong>{toSentenceCase(logItem[0])}</strong>
              const valueWithHTML = <span>{value}</span>
              return (
                <li key={item.id + logItem[0]}>
                  {keyWithHTML}
                  {': '}
                  {valueWithHTML}
                </li>
              )
            })}
          </ul>
        </HistoryInfo>
      </td>
    </tr>
  ))

  return loading ? (
    <TableHeader title={<Loading />} />
  ) : (
    <>
      {cardHistory.length ? (
        <TableHeader title={`${voucherWord(organisation)} history`} />
      ) : (
        <TableHeader title={`No ${voucherWord(organisation)} history`} />
      )}
      {renderHistory}
    </>
  )
}
export default History
